/* React-раздел в контракте страниц прототипа (app.html PAGES): { init, mount, unmount, mounted, S, fill, after, PREFIX, TESTS, … }.
   Каркас прототипа зовёт mount(#view) и тут же ждёт готовую разметку (сразу за ним — pop вида и after через 400 мс),
   поэтому первая отрисовка синхронная (flushSync). Каждый mount — свой корень React на #view; unmount снимает его до того,
   как каркас запишет в #view следующий раздел. */
import { StrictMode, type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { Providers } from './providers';
import { useAccountLocale } from '../i18n';

export interface PageSpec<S> {
  /** Объект в window: BotRewards */
  global: string;
  PREFIX: string;
  TESTS: [string, [string, string][]][];
  S: S;
  Page: ComponentType;
  /** Крючки страницы от каркаса (ctx): ui.spend, ui.go … */
  init?(ui: BotPageUi): void;
  /** Флаг меню «Состояния»; у «Студии» второй аргумент — значение флага */
  fill?(...args: unknown[]): void;
  after?(): void;
  /** Перед первой отрисовкой: раздел считается на экране */
  onMount?(): void;
  /** Перед снятием корня: закрыть общие листы и т. п. */
  beforeUnmount?(): void;
  /** Свои поля модуля (summary у «Наград») */
  extra?: Record<string, unknown>;
}

export function definePageModule<S>(spec: PageSpec<S>): BotPageModule {
  function LocalizedPage() { useAccountLocale(); return <spec.Page />; }
  let root: Root | null = null;
  const mod = {
    init(o?: Partial<BotPageUi>) {
      if (o) spec.init?.(o as BotPageUi);
    },
    mount(view: HTMLElement) {
      if (root) mod.unmount();
      spec.onMount?.();
      root = createRoot(view);
      const r = root;
      flushSync(() =>
        r.render(
          <StrictMode>
            <Providers>
              <LocalizedPage />
            </Providers>
          </StrictMode>,
        ),
      );
    },
    unmount() {
      if (!root) return;
      spec.beforeUnmount?.();
      const r = root;
      root = null;
      r.unmount();
    },
    get mounted() {
      return !!root;
    },
    S: spec.S,
    fill: (...a: unknown[]) => spec.fill?.(...a),
    after: () => spec.after?.(),
    PREFIX: spec.PREFIX,
    TESTS: spec.TESTS,
  };
  /* дескрипторы, а не раскрытие: у «Студии» side и sideOpen — живые геттеры, раскрытие сняло бы их один раз */
  if (spec.extra) {
    for (const [k, d] of Object.entries(Object.getOwnPropertyDescriptors(spec.extra))) {
      Object.defineProperty(mod, k, d);
    }
  }
  window[spec.global] = mod;
  return mod as unknown as BotPageModule;
}
